// Plane trees along the streets.
import * as THREE from 'three';
import { mergeGeometries, mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import { PI } from '../lib/math.js';
import { pick, rnd, rr } from '../lib/random.js';
import { scene } from '../core/scene.js';
import { M, canvasTex } from '../lib/factory.js';
import { noiseFill } from '../textures/cloth.js';
import { trees } from './eixample.js';
import { UP } from './sagrada.js';
import { streets } from './landmarks.js';

export let pits, treeTime;

export function buildTrees() {
  pits = [];
  for (const c of streets)
    for (let t = -43; t <= 43; t += 4) {
      if (streets.some((k) => Math.abs(t - k) < 7.8)) continue;
      for (const o of [-2.65, 2.65])
        for (const [x, z] of [
          [c + o, t],
          [t, c + o],
        ]) {
          trees.push({ x, z, s: rr(0.75, 0.95) });
          pits.push({ x, z });
        }
    }
  // plane trees: lumpy multi-blob crowns with a light-to-dark gradient, swaying in the wind
  treeTime = { value: 0 };
  {
    const lumps = [
      [0, 2.7, 0, 1],
      [0.65, 2.4, 0.25, 0.75],
      [-0.55, 2.5, -0.3, 0.72],
      [0.1, 3.3, -0.1, 0.68],
      [-0.2, 2.3, 0.55, 0.6],
      [0.45, 3.0, -0.5, 0.55],
      [-0.5, 3.1, 0.35, 0.55],
    ];
    let crownG = mergeGeometries(
      lumps.map(([x, y, z, r]) => new THREE.IcosahedronGeometry(r, 1).translate(x, y, z)),
    );
    const pos = crownG.attributes.position,
      cols = [];
    const hash = (x, y, z) => {
      const v = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719) * 43758.5453;
      return v - Math.floor(v);
    };
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i),
        y = pos.getY(i),
        z = pos.getZ(i);
      // hash of the rounded position, so vertices shared by two faces move together (no cracks)
      const n =
        (hash(+(x * 2).toFixed(0), +(y * 2).toFixed(0), +(z * 2).toFixed(0)) - 0.5) * 0.12 +
        Math.sin(x * 5 + z * 4) * Math.cos(y * 6) * 0.04;
      pos.setXYZ(i, x * (1 + n), y + n, z * (1 + n));
      const t = THREE.MathUtils.clamp((y - 1.9) / 1.9, 0, 1);
      cols.push(0.62 + t * 0.5, 0.66 + t * 0.45, 0.55 + t * 0.3);
    }
    crownG.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3));
    crownG.deleteAttribute('normal');
    crownG.deleteAttribute('uv');
    crownG = mergeVertices(crownG, 1e-3);
    crownG.computeVertexNormals();
    const crownMat = M(0xffffff, { vertexColors: true, flatShading: false, roughness: 0.8 });
    crownMat.onBeforeCompile = (sh) => {
      sh.uniforms.uTime = treeTime;
      sh.vertexShader =
        'uniform float uTime;\n' +
        sh.vertexShader.replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
      float ph = instanceMatrix[3].x * .37 + instanceMatrix[3].z * .23;
      transformed.x += sin(uTime * 1.6 + ph) * .05 * max(0., transformed.y - 1.8);
      transformed.z += cos(uTime * 1.3 + ph) * .04 * max(0., transformed.y - 1.8);`,
        );
    };
    const trunkG = mergeGeometries([
      new THREE.CylinderGeometry(0.11, 0.17, 2.2, 12).translate(0, 1.1, 0),
      new THREE.CylinderGeometry(0.035, 0.06, 1.1, 6).rotateZ(-0.9).translate(-0.42, 2.5, 0.1),
      new THREE.CylinderGeometry(0.035, 0.06, 1, 6).rotateX(0.8).translate(0, 2.55, 0.38),
      new THREE.CylinderGeometry(0.03, 0.055, 1, 6).rotateX(-0.85).translate(0.05, 2.5, -0.4),
      new THREE.CylinderGeometry(0.03, 0.05, 1.1, 6).rotateZ(0.25).translate(0.12, 2.9, 0),
      new THREE.CylinderGeometry(0.05, 0.08, 0.9, 5).rotateZ(0.7).translate(0.3, 2, 0),
      new THREE.CylinderGeometry(0.05, 0.08, 0.8, 5).rotateZ(-0.8).translate(-0.28, 2.1, 0.1),
    ]);
    const crowns = new THREE.InstancedMesh(crownG, crownMat, trees.length),
      trunks = new THREE.InstancedMesh(
        trunkG,
        M(0xffffff, {
          flatShading: false,
          map: canvasTex(128, 256, (c) => {
            // the camouflage bark of Barcelona's plane trees: cream, olive and grey patches
            c.fillStyle = '#bdb391';
            c.fillRect(0, 0, 128, 256);
            for (let k = 0; k < 90; k++) {
              c.fillStyle = pick(['#8f8a66', '#d8d1b4', '#7d7c5c', '#a9a27e', '#e2dcc4']);
              c.beginPath();
              c.ellipse(rnd() * 128, rnd() * 256, rr(6, 18), rr(8, 26), rr(0, PI), 0, PI * 2);
              c.fill();
            }
            noiseFill(c, 128, 256, 1500, ['rgba(60,55,40,.25)', 'rgba(255,255,240,.2)'], 1);
          }),
        }),
        trees.length,
      );
    const mtx = new THREE.Matrix4(),
      q = new THREE.Quaternion(),
      col = new THREE.Color(),
      greens = [0x7cb84a, 0x8fc455, 0x6aa840, 0xa2cf5e, 0x84bd4f];
    trees.forEach((t, i) => {
      q.setFromAxisAngle(UP, rr(0, PI * 2));
      mtx.compose(
        new THREE.Vector3(t.x, 0.3, t.z),
        q,
        new THREE.Vector3(t.s, t.s * rr(0.9, 1.15), t.s),
      );
      crowns.setMatrixAt(i, mtx);
      trunks.setMatrixAt(i, mtx);
      crowns.setColorAt(i, col.set(pick(greens)));
    });
    trunks.castShadow = trunks.receiveShadow = true;
    scene.add(trunks);
    const leafTex = canvasTex(256, 256, (c) => {
      for (let k = 0; k < 520; k++) {
        const x = 128 + rr(-110, 110),
          y = 128 + rr(-110, 110);
        if (Math.hypot(x - 128, y - 128) > 118) continue;
        const len = rr(7, 13),
          wid = len * rr(0.45, 0.6),
          v = rr(0, 1),
          dark = Math.hypot(x - 128, y - 128) < 60 ? 0.8 : 1;
        c.save();
        c.translate(x, y);
        c.rotate(rr(0, PI * 2));
        c.fillStyle = `rgb(${Math.floor((70 + v * 70) * dark)},${Math.floor((115 + v * 75) * dark)},${Math.floor((40 + v * 30) * dark)})`;
        c.beginPath();
        c.moveTo(-len, 0);
        c.quadraticCurveTo(0, -wid, len, 0);
        c.quadraticCurveTo(0, wid, -len, 0);
        c.fill();
        c.strokeStyle = 'rgba(30,50,20,.45)';
        c.lineWidth = 0.8;
        c.beginPath();
        c.moveTo(-len, 0);
        c.lineTo(len, 0);
        c.stroke();
        c.restore();
      }
    });
    const CARDS = 108,
      cardGeo = new THREE.PlaneGeometry(1.05, 1.05);
    const cardMat = M(0xffffff, {
      map: leafTex,
      emissiveMap: leafTex,
      emissive: 0x6a8a40,
      emissiveIntensity: 0.16,
      alphaTest: 0.5,
      side: THREE.DoubleSide,
      flatShading: false,
      roughness: 0.75,
    });
    // each card is shaded with the normal of the crown's ellipsoid at its position (classic foliage trick), and sways
    cardMat.onBeforeCompile = (sh) => {
      sh.uniforms.uTime = treeTime;
      sh.vertexShader =
        'uniform float uTime;\nattribute vec3 aFoliageN;\n' +
        sh.vertexShader
          .replace(
            '#include <begin_vertex>',
            `#include <begin_vertex>
        float ph = instanceMatrix[3].x * .37 + instanceMatrix[3].z * .23;
        transformed.x += sin(uTime * 1.6 + ph) * .04; transformed.z += cos(uTime * 1.3 + ph) * .03;`,
          )
          .replace(
            '#include <defaultnormal_vertex>',
            '#include <defaultnormal_vertex>\ntransformedNormal = normalize((viewMatrix * vec4(aFoliageN, 0.0)).xyz);',
          );
      sh.fragmentShader = sh.fragmentShader.replace(
        '#include <normal_fragment_begin>',
        THREE.ShaderChunk.normal_fragment_begin.replace('normal *= faceDirection;', ''),
      );
    };
    const cards = new THREE.InstancedMesh(cardGeo, cardMat, trees.length * CARDS),
      fn = new Float32Array(trees.length * CARDS * 3);
    const tm = new THREE.Matrix4(),
      lm = new THREE.Matrix4(),
      lq = new THREE.Quaternion(),
      le = new THREE.Euler(),
      dir = new THREE.Vector3();
    const tp = new THREE.Vector3(),
      tq = new THREE.Quaternion(),
      ts = new THREE.Vector3(),
      n = new THREE.Vector3();
    trees.forEach((t, i) => {
      crowns.getMatrixAt(i, tm);
      tm.decompose(tp, tq, ts);
      for (let k = 0; k < CARDS; k++) {
        // clusters biased towards the outer shell of the crown
        dir.randomDirection();
        const r = 0.35 + 0.95 * Math.sqrt(rnd()),
          j = i * CARDS + k;
        const p = new THREE.Vector3(dir.x * r * 1.45, 2.85 + dir.y * r * 1.05, dir.z * r * 1.45);
        le.set(rr(0, PI), rr(0, PI * 2), rr(0, PI));
        lq.setFromEuler(le);
        lm.compose(p, lq, new THREE.Vector3().setScalar(rr(0.85, 1.3)));
        cards.setMatrixAt(j, lm.premultiply(tm));
        n.set(dir.x / 1.45, dir.y / 1.05 + 0.35, dir.z / 1.45)
          .normalize()
          .applyQuaternion(tq);
        fn.set([n.x, n.y, n.z], j * 3);
        cards.setColorAt(
          j,
          col.set(pick(greens)).multiplyScalar((r < 0.8 ? 0.72 : 1) * (dir.y > 0 ? 1.08 : 0.86)),
        );
      }
    });
    cardGeo.setAttribute('aFoliageN', new THREE.InstancedBufferAttribute(fn, 3));
    cards.castShadow = cards.receiveShadow = true;
    scene.add(cards);
  }
}
